<template>
  <div class="search-result">
    <!-- Tab导航 -->
    <div class="search-tabs">
      <span
        v-for="tabItem in tabList"
        :key="tabItem.value"
        :class="{ active: searchStore.currentTab === tabItem.value }"
        @click="handleTabChange(tabItem.value)"
      >
        {{ t(`search.tabs.${tabItem.value}`) }}
      </span>
    </div>

    <!-- 搜索结果列表 -->
    <div class="search-result-list" :class="searchStore.currentTab">
      <Empty
        v-if="searchStore.currentList.length === 0"
        :tip="t('search.empty')"
      />
      <template v-else>
        <template v-if="searchStore.currentTab === Tab.Post">
          <div class="filter-bar">
            <span class="related-title">{{ t("search.relatedPosts") }}</span>
            <div class="filter-options">
              <span
                v-for="type in [FilterType.Views, FilterType.Dateline]"
                :key="type"
                :class="{ active: searchStore.filterType === type }"
                @click="handleFilterChange(type)"
              >
                {{ t(`search.filter.${type}`) }}
              </span>
            </div>
          </div>
          <div class="post-list">
            <ThreadItem
              v-for="(post, index) in searchStore.threads"
              :key="post.id"
              :post="post"
              :show-follow="true"
              @update:post="
                (updatedPost) => updateSearchPost(index, updatedPost)
              "
            />
          </div>
        </template>
        <template v-else>
          <div class="user-list">
            <UserItem
              v-for="user in searchStore.users"
              :key="user.id"
              :user="user"
              @follow="handleUserFollow"
            />
          </div>
        </template>
      </template>
      <div ref="loadMoreRef" class="load-more-trigger"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useSearchStore } from "@/stores";
import { useUserStore } from "@/stores";
import { useToastStore } from "@/stores";
// @ts-ignore
import ThreadItem from "@/components/common/ThreadItem.vue";
// @ts-ignore
import Empty from "@/components/common/Empty.vue";
// @ts-ignore
import UserItem from "@/components/common/UserItem.vue";
import { clickButton, pageType } from "@/utils/point";

enum Tab {
  Post = "post",
  User = "user",
}

enum FilterType {
  Views = "views",
  Dateline = "dateline",
}

interface Props {
  keyword: string;
}

const props = defineProps<Props>();
const { t } = useI18n();
const searchStore = useSearchStore();
const userStore = useUserStore();
const toast = useToastStore();

// IntersectionObserver
const loadMoreRef = ref<HTMLElement | null>(null);
const observer = ref<IntersectionObserver | null>(null);

const tabList = [{ value: Tab.Post }, { value: Tab.User }];

// 加载更多
const loadMore = () => {
  if (searchStore.currentLoadingMore || !searchStore.currentHasMore) {
    return;
  }
  searchStore.loadMoreCurrentTab(props.keyword);
};

// 初始化 IntersectionObserver
const initIntersectionObserver = () => {
  if (observer.value) {
    observer.value.disconnect();
  }

  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    },
    {
      rootMargin: "100px", // 提前100px触发加载
      threshold: 0.1,
    }
  );

  if (loadMoreRef.value) {
    observer.value.observe(loadMoreRef.value);
  }
};

// Tab 切换
const handleTabChange = async (newTab: Tab) => {
  clickButton({
    eventType: pageType.SEARCH,
    eventReason: "切换搜索结果分类",
    eventValue: JSON.stringify({
      tab: newTab,
    }),
  });
  if (searchStore.currentTab === newTab) return;
  searchStore.setCurrentTab(newTab);
  // 如果当前 tab 没有数据，则搜索
  await searchStore.searchCurrentTab(props.keyword);
};

// 筛选切换
const handleFilterChange = async (type: FilterType) => {
  if (searchStore.filterType === type) return;
  clickButton({
    eventType: pageType.SEARCH,
    eventReason: "切换搜索结果排序",
    eventValue: JSON.stringify({
      filter: type,
    }),
  });
  searchStore.setFilterType(type);
  searchStore.resetThreads();
  await searchStore.searchCurrentTab(props.keyword);
};

// 处理用户关注
const handleUserFollow = async (user: any) => {
  const uid = user.uid;
  const isFollowed = user.is_following;

  if (!uid) return;

  if (isFollowed) {
    const res = await userStore.unfollow(uid);
    if (res?.code == 200) {
      // 更新本地状态
      user.is_following = false;
      toast.showToast(t("profile.unfollowSuccess"), "success", 1000);
    }
  } else {
    const res = await userStore.follow(uid);
    if (res?.code == 200) {
      // 更新本地状态
      user.is_following = true;
      toast.showToast(t("profile.followSuccess"), "success", 1000);
    }
  }
};

// 更新搜索帖子
const updateSearchPost = (index: number, updatedPost: any) => {
  searchStore.threads[index] = updatedPost;
  searchStore.updateSearchThreadsFollowStatus(
    updatedPost.authorid,
    updatedPost.is_author_followed
  );
};

onMounted(() => {
  nextTick(() => {
    initIntersectionObserver();
  });
});

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});
</script>

<style scoped lang="scss">
.search-result {
  width: 100%;
}

.search-tabs {
  display: flex;
  border-bottom: 1px solid #eee;
  margin-bottom: 5px;

  span {
    flex: 1;
    text-align: center;
    padding: 12px 0;
    font-size: 16px;
    color: #999;
    background: #fff;
    cursor: pointer;

    &.active {
      color: #222;
      font-weight: bold;
      border-bottom: 2px solid #dd8632;
    }
  }
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  gap: 16px;
  max-width: 800px;
  margin: 0 auto;
  font-size: 14px;

  .related-title {
    color: #444;
    font-weight: 500;
  }

  .filter-options {
    display: flex;
    gap: 16px;
  }

  span {
    color: #666;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      color: #333;
    }

    &.active {
      color: #dd8632;
      font-weight: 600;
    }
  }
}

.post-list {
  padding: 0 12px;
}

.user-list {
  background-color: #fff;
  display: flex;
  flex-direction: column;
}

.load-more-trigger {
  height: 1px;
  opacity: 0;
}
</style>
